import React, {
	FC, memo, useEffect, useState,
} from 'react';
import {
	filter, merge, Subject, takeUntil, tap,
} from 'rxjs';

import { Modal, Button } from '@sololearnorg/sol';
import useStyles from 'isomorphic-style-loader/useStyles';
import { Container } from '../../../../../symphony';

import { SlDataService } from '../../../services/sl-data.service';
import { SlActions } from '../../../services/ActionsService/sl-actions';
import {
	ActionType,
	ILessonQuitPromptInfo,
	LessonQuitPopupType,
	ModalKeys,
	RequestType,
} from '../../../global-interface';
import { SlLEContext } from '../../../global-constants';

import s from './LessonQuitPrompt.scss';

export const LessonQuitPrompt: FC = memo(() => {
	useStyles(s);
	const [showQuitPromptModal, setShowQuitPromptModal] = useState<boolean>(false);
	const [quitPromptInfo, setQuitPromptInfo] = useState<ILessonQuitPromptInfo>(null);

	const { actions$ } = Container.take(SlLEContext, SlActions);

	useEffect(() => {
		const { lessonQuitPromptInfo$, modals$ } = Container.take(
			SlLEContext,
			SlDataService,
		);
		const unsubscriber = new Subject<void>();

		actions$.next({
			type: ActionType.defineLessonQuitPromptInfo,
			requestType: RequestType.Cancelable,
		});

		merge(
			modals$.pipe(
				tap((modals) => {
					if (modals?.[ModalKeys.LessonQuitPrompt] !== undefined) {
						setShowQuitPromptModal(
							modals[ModalKeys.LessonQuitPrompt],
						);
					}
				}),
			),
			lessonQuitPromptInfo$.pipe(
				tap((info: ILessonQuitPromptInfo) => {
					if (info?.shouldShow) {
						window.onbeforeunload = function () {
							return true;
						};
					}
					setQuitPromptInfo(info);
				}),
			),
		)
			.pipe(
				filter((d) => !!d),
				takeUntil(unsubscriber),
			)
			.subscribe();

		return () => {
			actions$.next({
				type: ActionType.OnLessonQuitPromptUnmount,
				requestType: RequestType.Parallel,
			});
			window.onbeforeunload = null;
			unsubscriber.next();
			unsubscriber.complete();
		};
	}, []);

	const onContinueLearning = (): void => {
		actions$.next({
			type: ActionType.OnLessonQuitPromptContinueClick,
			requestType: RequestType.Parallel,
		});
	};

	const onLeaveLesson = (): void => {
		actions$.next({
			type: ActionType.OnLessonQuitPromptLeaveClick,
			requestType: RequestType.Parallel,
		});
	};

	const defineModal = () => (quitPromptInfo.type === LessonQuitPopupType.LOSE_PROGRESS ? (
		<Modal.Default
			className="sl-lesson-quit-prompt"
			onClose={onContinueLearning}
			onClickOutside={() => {}}
			actions={[
				<Button
					buttonType="secondary"
					onClick={onLeaveLesson}
					key={1}
				>
					{quitPromptInfo.popupData.negativeButtonTitle}
				</Button>,
				<Button
					buttonType="primary"
					onClick={onContinueLearning}
					key={2}
				>
					{quitPromptInfo.popupData.positiveButtonTitle}
				</Button>,
			]}
			headline={quitPromptInfo.popupData.title}
			description={quitPromptInfo.popupData.description}
		/>
	) : (
		<Modal.Default
			className="sl-lesson-quit-prompt"
			onClose={onContinueLearning}
			actions={[
				<Button
					buttonType="secondary"
					onClick={onContinueLearning}
					key={1}
				>
					{quitPromptInfo.popupData.positiveButtonTitle}
				</Button>,
				<Button
					buttonType="primary"
					onClick={onLeaveLesson}
					key={2}
				>
					{quitPromptInfo.popupData.negativeButtonTitle}
				</Button>,
			]}
			headline={quitPromptInfo.popupData.title}
		/>
	));

	return showQuitPromptModal && quitPromptInfo?.popupData
		? defineModal()
		: null;
});
